import {
  faArrowRightLong, faBoltLightning, faChartLine, faEye, faEyeSlash, faLock,
  faTriangleExclamation, faUser, faWaveSquare
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useCookies } from "react-cookie";
import HeaderHome from "../../Components/Header_Home/Header_Home";
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import "./Login.css";

const VERSION = "v1.2.5";

const Login = () => {

  const [cookies] = useCookies();
  const en = cookies?.language === "en";

  const [inputs, setInputs] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // One line for each half of the platform, plus the part that serves both.
  const points = [
    { icon: faWaveSquare, ro: "Joasă tensiune: desen, funcționare, depanare", en: "Low tension: drawing, operation, fault-finding" },
    { icon: faBoltLightning, ro: "Înaltă tensiune: manevre pe schema stației", en: "High tension: maneuvers on the station scheme" },
    { icon: faChartLine, ro: "Progres urmărit de profesor", en: "Progress tracked by your teacher" },
  ];

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: inputs.username,
          password: inputs.password
        })
      };

      const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, options);
      const response = await request.json();
      if (response.success) {
        // Auth is carried by the HttpOnly `token` cookie set by the server.
        window.history.replaceState({
          page: "categories"
        }, "");
        window.location.replace("/categories");
      } else {
        setError(en ? response.message.en : response.message.ro);
        setLoading(false);
      }
    } catch (e) {
      setError(en ? "The server could not be reached." : "Serverul nu a putut fi contactat.");
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs(currInputs => {
      return {
        ...currInputs,
        [e.target.name]: e.target.value
      };
    });
  };

  return (
    <div className="login-shell">

      {/* Same top bar as the public pages (home / about / contact). */}
      <HeaderHome data={null} activePage={null} />

      <div className="login-page">
        <div className="login-card">

          {/* Showcase pane: the station photo behind a pane of glass, same
              treatment as the module preview on the picker. */}
          <aside className="login-showcase">
            <div className="login-brand">
              <img src={ZenLogoWhite} alt="ZenVolt" />
              <span>ZenVolt</span>
              <span className="login-version">{VERSION}</span>
            </div>

            <div className="login-showcase-caption">
              <h2>{en ? "Your lab, straight in the browser" : "Laboratorul tău, direct în browser"}</h2>
              <p>
                {en
                  ? "Low tension and high tension in the same place: draw a scheme, watch it work, find the fault — then step up to the station."
                  : "Joasă și înaltă tensiune în același loc: desenezi o schemă, o vezi funcționând, cauți defectul — apoi treci la stație."}
              </p>
            </div>

            <ul className="login-points">
              {points.map((p) => (
                <li key={p.en}>
                  <span className="login-point-icon"><FontAwesomeIcon icon={p.icon} /></span>
                  {en ? p.en : p.ro}
                </li>
              ))}
            </ul>
          </aside>

          <form className="login-form" onSubmit={login}>
            <p className="login-kicker">
              <span className="login-kicker-dot" />
              {en ? "Sign in" : "Autentificare"}
            </p>

            <h2 className="login-title">{en ? "Welcome back" : "Bine ai revenit"}</h2>
            <p className="login-sub">
              {en
                ? "Sign in with the account your teacher set up for you."
                : "Intră în cont cu datele primite de la profesorul tău."}
            </p>

            <div className="login-field">
              <FontAwesomeIcon icon={faUser} className="login-field-icon" />
              <input
                type="text"
                name="username"
                value={inputs.username}
                onChange={handleInputChange}
                placeholder={en ? "Username" : "Nume de utilizator"}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="login-field">
              <FontAwesomeIcon icon={faLock} className="login-field-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={inputs.password}
                onChange={handleInputChange}
                placeholder={en ? "Password" : "Parola"}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-eye"
                title={showPassword ? (en ? "Hide password" : "Ascunde parola") : (en ? "Show password" : "Arată parola")}
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword(curr => !curr)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>

            {error.length
              ? <p className="login-error">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {error}
                </p>
              : null}

            <button type="submit" className="login-submit" disabled={loading}>
              {loading
                ? (en ? "Signing in…" : "Se conectează…")
                : <>{en ? "Log in" : "Intră în cont"} <FontAwesomeIcon icon={faArrowRightLong} /></>}
            </button>

            <p className="login-hint">
              {en
                ? "Forgot your password? Ask your teacher to reset it from the users page."
                : "Ai uitat parola? Cere-i profesorului să o reseteze din pagina de utilizatori."}
            </p>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;
